import { useEffect } from 'react';
import { goTo } from '../utils/angularUtils';

const STORAGE_KEY = 'redirect-on-reload';

/**
 * Redirects to a given path if the page was reloaded (F5, Cmd+R, etc.).
 *
 * How it works:
 * 1. On mount, sets a sessionStorage flag.
 * 2. On normal SPA unmount, cleanup removes the flag.
 * 3. On page reload, React cleanup does NOT run — flag persists in sessionStorage.
 * 4. Next mount detects the flag and redirects via goTo().
 *
 * sessionStorage is used because it survives reload but clears when the tab is closed.
 * Each tab has its own sessionStorage, so multiple tabs work independently.
 *
 * Note: the redirect happens in useEffect, so the component may briefly render
 * and trigger init API calls before the redirect completes.
 *
 * @param redirectPath - path to redirect to on reload
 */
export const useRedirectOnReload = (redirectPath: string) => {
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) {
      sessionStorage.removeItem(STORAGE_KEY);
      goTo(redirectPath, { replaceHistory: true });
    } else {
      sessionStorage.setItem(STORAGE_KEY, 'true');
      return () => sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [redirectPath]);
};
